import { useEffect } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Grid, Typography } from '@mui/material';

import { PermissionsEnum, SystemModulesEnum } from '@/auth/interfaces';
import { EstadosActivacionInstallOTEnumChoice, ROUTER_PATHS, gridSizeMdLg12 } from '@/shared';
import { useGetOrdenTrabajo } from '@/shared/actions/erp';
import { hasSystemModule } from '@/shared/utils';
import { useCheckPermissions, useLoaders } from '@/shared/hooks';
import { AlertSeverityEnum } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { SaveActivacionInstallPendingOT } from '@/erp/operaciones/activaciones/instalaciones/shared/components';
import FotosTrasladoTab from '@/erp/tecnico/orden-trabajo/shared/components/tables/fotos/traslado/FotosTrasladoTab';
import ConceptosTrasladoActivacionesCard from '../../shared/components/ConceptosTrasladoActivacionesCard';
import Vision360TrasladoCard from '../../shared/components/Vision360TrasladoCard';

/**
 * Revisión de un TRASLADO ya gestionado. Reutiliza el formulario completo de
 * instalaciones, redirigiendo a las rutas de traslado. Las secciones propias
 * del traslado (evidencia fotográfica y otros conceptos) van DENTRO del
 * formulario, en orden, vía `seccionesAdicionales`. Si el técnico agregó
 * conceptos tras iniciar, aquí se aprueban (mientras la OT no esté REALIZADA).
 */
const ActivacionTrasladoGestionadaPage: React.FC = () => {
  useCheckPermissions(PermissionsEnum.api_change_ordentrabajo);
  const returnUrl = ROUTER_PATHS.activaciones.activacionesTrasladoNav;
  const navigate = useNavigate();
  const setAlert = useUiStore((s) => s.setAlert);

  const { id } = useParams();
  const { data, isLoading, isRefetching } = useGetOrdenTrabajo(id!);
  useLoaders(isLoading || isRefetching);

  useEffect(() => {
    if (!data?.data?.id_orden_trabajo) return;
    if (
      data?.data?.estado_activacion !==
      EstadosActivacionInstallOTEnumChoice.GESTIONADA
    ) {
      setAlert(
        true,
        'La orden de trabajo no tiene el estado correcto',
        AlertSeverityEnum.error,
      );
      navigate(returnUrl);
    }
  }, [data?.data?.id_orden_trabajo]);

  if (isLoading) return null;
  if (!data?.data?.id_orden_trabajo) return <Navigate to={returnUrl} />;

  return (
    <SaveActivacionInstallPendingOT
      titlePage="Activación - Traslado Gestionada"
      ordenTrabajo={data.data}
      customReturnUrl={returnUrl}
      seccionesAdicionales={
        <>
          {/* Visión 360 del cliente (FibraPoints, saldo, pagos, tickets), como en visitas. */}
          <Grid {...gridSizeMdLg12} container item spacing={2} mb={3}>
            <Grid {...gridSizeMdLg12} item py={2}>
              <Typography variant="h6">VISIÓN 360 DEL CLIENTE</Typography>
            </Grid>
            <Grid {...gridSizeMdLg12} item>
              <Vision360TrasladoCard ordenTrabajo={data.data} />
            </Grid>
          </Grid>

          {/* Evidencia fotográfica del traslado, en vivo. Activaciones puede
              ELIMINAR fotos (el técnico deberá volver a subirlas). */}
          <Grid {...gridSizeMdLg12} container item spacing={2} mb={3}>
            <Grid {...gridSizeMdLg12} item py={2}>
              <Typography variant="h6">EVIDENCIA FOTOGRÁFICA DEL TRASLADO</Typography>
              <Typography variant="caption" color="text.secondary">
                Fotos del técnico: 6 de ingreso, 2 de proceso (punto inicial y punto final) y 6 de finalización.
                Se actualizan solas mientras el técnico trabaja.
              </Typography>
            </Grid>
            <Grid {...gridSizeMdLg12} item>
              <FotosTrasladoTab
                ordenTrabajo={data.data}
                readOnly
                // Activación finalizada (GESTIONADA): la evidencia queda sellada,
                // ya no se pueden eliminar fotos.
                canDelete={false}
              />
            </Grid>
          </Grid>

          {/* Otros conceptos del traslado: aprobar los agregados por el técnico. */}
          <Grid {...gridSizeMdLg12} container item spacing={2} mb={3}>
            <Grid {...gridSizeMdLg12} item py={2}>
              <Typography variant="h6">OTROS CONCEPTOS DEL TRASLADO</Typography>
            </Grid>
            <Grid {...gridSizeMdLg12} item>
              <ConceptosTrasladoActivacionesCard
                ordenTrabajo={data.data}
                readOnly={data.data?.estado_orden_trabajo === 'REALIZADA'}
              />
            </Grid>
          </Grid>
        </>
      }
    />
  );
};

export default ActivacionTrasladoGestionadaPage;
